import { useRef, useState } from 'react';
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Clock, Calendar, MapPin, ShieldCheck } from 'lucide-react';
import { PHOTOS, WALK_PLANS, LOGO, formatPrice } from '@/data/site';
import { useLive } from '@/lib/experience';
import { usePlanModal } from './ContactModal';
import { SectionHeading } from './fx/SectionHeading';
import { Photo } from './fx/Photo';
import { Magnetic } from './fx/Magnetic';
import { Reveal } from './fx/Reveal';
import { Wave } from './fx/Wave';
import { burst } from './fx/Burst';
import { Paw, Cloudy, Bone } from './brand/Shapes';

const SKY = '#D4ECFD';
const PAWS = 14; // pegadas na trilha

// planos com as cores da paleta: avulso rosa, semanal azul, mensal lilás
const PLAN_STYLE = {
  avulso: { Icon: Clock, chip: 'bg-pet-pink-soft text-pet-pink-deep', bone: 'text-pet-pink', shade: '#F29A94', ink: 'text-pet-pink-ink', btn: 'btn-pink' },
  semanal: { Icon: Calendar, chip: 'bg-pet-sky text-pet-blue', bone: 'text-pet-sky', shade: '#A9CFF0', ink: 'text-pet-blue-deep', btn: 'btn-blue' },
  mensal: { Icon: MapPin, chip: 'bg-[#E6DEFA] text-[#6B4FC0]', bone: 'text-pet-lilac', shade: '#A792E0', ink: 'text-[#4B2F8F]', btn: 'btn-lilac' },
};

const CLOUDS = [
  { top: '3%', w: 150, dur: 70, delay: -10, o: 0.85 },
  { top: '12%', w: 90, dur: 55, delay: -35, o: 0.7 },
  { top: '22%', w: 110, dur: 80, delay: -55, o: 0.6 },
];

/** Preço dentro de um ossinho */
const BonePrice = ({ value, boneClass, inkClass, shade }) => (
  <span className="relative inline-flex items-center justify-center w-[6.4rem] md:w-[9.5rem] shrink-0 -rotate-6">
    <Bone className={`w-full ${boneClass}`} style={{ filter: `drop-shadow(0 4px 0 ${shade})` }} />
    <span className={`absolute inset-0 flex items-center justify-center font-display text-[1.35rem] md:text-[2.1rem] leading-none tabular-nums ${inkClass}`}>
      {value}
    </span>
  </span>
);

export const Passeio = () => {
  const { openPlan } = usePlanModal();
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const photoRef = useRef(null);
  const routeRef = useRef(null);
  const [steps, setSteps] = useState(0);
  const live = useLive(sectionRef);

  const { scrollYProgress: photoProgress } = useScroll({ target: photoRef, offset: ['start end', 'end start'] });
  const photoY = useTransform(photoProgress, [0, 1], ['-6%', '6%']);
  const photoRotate = useTransform(photoProgress, [0, 1], [-5, 2]);

  const { scrollYProgress: route } = useScroll({ target: routeRef, offset: ['start 75%', 'end 60%'] });
  useMotionValueEvent(route, 'change', (v) => setSteps(Math.round(v * PAWS)));
  const walkerTop = useTransform(route, [0, 1], ['0%', '94%']);
  const walkerOpacity = useTransform(route, [0, 0.88, 0.96], [1, 1, 0]);
  const shown = reduce ? PAWS : steps;
  const reached = (i) => reduce || shown >= Math.round((i / WALK_PLANS.length) * PAWS) + 1;

  return (
    <section
      ref={sectionRef}
      data-paused={!live}
      id="passeio"
      data-nav="passeio"
      className="relative overflow-x-clip"
      style={{ background: `linear-gradient(180deg, ${SKY} 0%, #C5E4FC 100%)` }}
    >
      <Wave color={SKY} className="absolute bottom-[calc(100%-1px)] inset-x-0" />

      {/* nuvens passeando (só no alto do céu) e sol sorridente */}
      <div className="absolute inset-x-0 top-0 h-[34%] overflow-hidden pointer-events-none" aria-hidden="true">
        {CLOUDS.map((c, i) => (
          <div
            key={i}
            className="absolute left-0 text-white"
            style={{ top: c.top, width: c.w, opacity: c.o, animation: `drift ${c.dur}s linear ${c.delay}s infinite` }}
          >
            <Cloudy className="w-full" />
          </div>
        ))}
      </div>
      <motion.div
        className="absolute right-[5%] top-[3%] w-20 h-20 md:w-28 md:h-28 pointer-events-none"
        animate={live && !reduce ? { rotate: 360 } : { rotate: 0 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {[...Array(12)].map((_, i) => (
            <rect key={i} x="47" y="2" width="6" height="16" rx="3" fill="#FFD166" transform={`rotate(${i * 30} 50 50)`} />
          ))}
          <circle cx="50" cy="50" r="26" fill="#FFD166" />
          <circle cx="42" cy="47" r="2.5" fill="#8A5A00" />
          <circle cx="58" cy="47" r="2.5" fill="#8A5A00" />
          <path d="M42 56 Q50 62 58 56" stroke="#8A5A00" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <circle cx="36" cy="54" r="3.5" fill="#FF8A9B" opacity="0.6" />
          <circle cx="64" cy="54" r="3.5" fill="#FF8A9B" opacity="0.6" />
        </svg>
      </motion.div>

      <div className="container-cp relative py-20 md:py-28">
        <SectionHeading
          align="center"
          kicker="Serviços de passeio"
          icon={MapPin}
          kickerClass="bg-pet-peach text-[#8A4B2A]"
          parts={[{ text: 'Passeio' }, { text: 'ao ar livre!', className: 'text-pet-blue sticker-text' }]}
          note="Seu pet merece se exercitar e se divertir ao ar livre 🌳"
          className="mb-12 md:mb-16"
        />

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* polaroid do passeio */}
          <div ref={photoRef} className="relative min-w-0 lg:col-span-5 lg:sticky lg:top-[14vh] px-3">
            <motion.figure
              className="relative bg-white p-3 pb-4 md:p-4 md:pb-5 rounded-[18px] shadow-[0_30px_60px_-24px_rgba(45,55,72,0.45)] max-w-[400px] mx-auto"
              style={{ rotate: reduce ? -3 : photoRotate }}
              whileHover={{ rotate: 0, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 260, damping: 14 }}
              data-cursor="Passeio!"
            >
              <span className="absolute -top-3 left-8 w-24 h-7 bg-pet-pink/80 rotate-[-8deg] rounded-sm shadow-sm" aria-hidden="true" />
              <span className="absolute -top-3 right-8 w-20 h-7 bg-pet-mint/90 rotate-[10deg] rounded-sm shadow-sm" aria-hidden="true" />
              <div className="relative aspect-[4/5] rounded-[12px] overflow-hidden">
                <motion.div className="absolute inset-[-8%]" style={{ y: reduce ? 0 : photoY }}>
                  <Photo photo={PHOTOS.passeioNoite} sizes="(min-width: 1024px) 40vw, 90vw" className="w-full h-full object-cover" />
                </motion.div>
              </div>
              <figcaption className="mt-3 text-center font-hand text-2xl text-pet-ink">passeio no lago à noite 🌙</figcaption>
            </motion.figure>

            {/* segunda polaroid: hora da bolinha */}
            <Reveal className="relative z-10 -mt-24 sm:-mt-28 ml-auto mr-0 sm:mr-4 w-[46%] max-w-[190px]" rotate={16} y={40}>
              <motion.figure
                className="relative bg-white p-2 pb-3 rounded-[14px] shadow-[0_24px_40px_-18px_rgba(45,55,72,0.5)]"
                style={{ rotate: 7 }}
                whileHover={{ rotate: 0, scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300, damping: 12 }}
                data-cursor="Bolinha!"
              >
                <span className="absolute -top-2.5 left-1/2 w-14 h-5 bg-pet-yellow/80 rounded-sm shadow-sm" style={{ transform: 'translateX(-50%) rotate(-6deg)' }} aria-hidden="true" />
                <div className="aspect-[4/5] rounded-[9px] overflow-hidden">
                  <Photo photo={PHOTOS.passeioBolinha} sizes="190px" className="w-full h-full object-cover object-[50%_40%]" />
                </div>
                <figcaption className="mt-1.5 text-center font-hand text-lg leading-none text-pet-ink">hora da bolinha 🎾</figcaption>
              </motion.figure>
            </Reveal>

            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {[
                { t: 'Buscamos no local combinado', I: MapPin },
                { t: 'Cerca de 1 hora', I: Clock },
                { t: 'Passeio seguro', I: ShieldCheck },
              ].map(({ t, I }, i) => (
                <Reveal as="span" key={t} delay={0.1 + i * 0.1} y={16} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-extrabold text-pet-ink shadow-sm">
                  <I className="w-4 h-4 text-pet-blue" /> {t}
                </Reveal>
              ))}
            </div>
          </div>

          {/* trilha de patinhas com os planos */}
          <div ref={routeRef} className="min-w-0 lg:col-span-7 relative pl-12 md:pl-20">
            <div className="absolute left-[8px] md:left-[22px] top-2 bottom-2 w-9" aria-hidden="true">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[4px] rounded-full bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.95)_0_10px,transparent_10px_20px)]" />
              {Array.from({ length: PAWS }).map((_, i) => (
                <motion.span
                  key={i}
                  className="absolute w-4 h-4 md:w-5 md:h-5 text-pet-pink-deep/80"
                  style={{ top: `${(i / (PAWS - 1)) * 96}%`, left: i % 2 ? '58%' : '4%', rotate: i % 2 ? 12 : -12 }}
                  initial={false}
                  animate={{ scale: i < shown ? 1 : 0, opacity: i < shown ? 1 : 0 }}
                  transition={{ type: 'spring', stiffness: 600, damping: 14 }}
                >
                  <Paw className="w-full h-full" />
                </motion.span>
              ))}
              {/* patinha que "anda" pela trilha junto com a rolagem */}
              <motion.span
                className="absolute z-0 left-1/2 -ml-5 w-10 h-10 rounded-full bg-white shadow-lg ring-4 ring-pet-pink flex items-center justify-center text-xl"
                style={{ top: reduce ? '94%' : walkerTop, opacity: reduce ? 0 : walkerOpacity }}
              >
                <motion.span animate={live ? { rotate: [-10, 10, -10] } : { rotate: 0 }} transition={{ duration: 0.5, repeat: Infinity }}>
                  🐕
                </motion.span>
              </motion.span>
            </div>

            <ol className="relative space-y-6 md:space-y-8">
              {WALK_PLANS.map((plan, i) => {
                const st = PLAN_STYLE[plan.id];
                const on = reached(i);
                return (
                  <li key={plan.id} className="relative">
                    <motion.span
                      className={`absolute z-10 -left-[50px] md:-left-[76px] top-7 w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center ring-4 ring-white shadow-md ${st.chip}`}
                      animate={{ scale: on ? 1 : 0.7, rotate: on ? 0 : -20 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 12 }}
                    >
                      <st.Icon className="w-5 h-5 md:w-6 md:h-6" />
                    </motion.span>

                    <Reveal y={30} rotate={2}>
                      <motion.div
                        className="rounded-[32px] bg-white p-5 md:p-7 shadow-[0_20px_50px_-30px_rgba(45,55,72,0.5)]"
                        whileHover={{ y: -6, rotate: i % 2 ? 1 : -1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 14 }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-display font-medium text-[1.8rem] md:text-[2.6rem] leading-none text-pet-ink">{plan.title}</h3>
                            <p className="mt-1.5 font-extrabold text-pet-ink/60">{plan.detail}</p>
                          </div>
                          <BonePrice value={formatPrice(plan.price)} boneClass={st.bone} inkClass={st.ink} shade={st.shade} />
                        </div>
                        <div className="mt-5">
                          <Magnetic>
                            <button
                              type="button"
                              onClick={(e) => {
                                burst(e, 'paw');
                                openPlan(`Passeio - ${plan.title}`);
                              }}
                              className={`${st.btn} min-h-[48px] px-5 text-[15px]`}
                            >
                              <Paw className="w-4 h-4" /> Quero esse
                            </button>
                          </Magnetic>
                        </div>
                      </motion.div>
                    </Reveal>
                  </li>
                );
              })}
            </ol>

            <Reveal className="relative z-10 mt-10 flex items-end gap-2" y={20}>
              <img src={LOGO} alt="" className="w-10 h-10 rounded-full ring-2 ring-white shrink-0" />
              <p className="rounded-[24px] rounded-bl-md bg-white px-5 py-4 max-w-md shadow-sm font-bold text-pet-ink/80 leading-relaxed">
                Buscamos seu pet no local combinado e fazemos um passeio seguro de cerca de 1 hora. 🐕‍🦺
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Passeio;
